import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { SWReactIcons } from "@similarweb/icons";

import { getSiPackageHref } from "app/common/hrefs";
import { swSettings } from "app/common/services/swSettings";
import { historyHookPreselect } from "app/contexts/AddonStateContext/const";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    StyledGIF,
    BannerWrapper,
    StyledBanner,
    SubtitleWrapper,
} from "app/pages/segments/upsale-banner/SegmentsUpsaleBanner.styled";
import { AssetsService } from "app/services/AssetsService";
import { openHooksModalV5 } from "app/services/ModalService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { allTrackers } from "app/services/track/track";
import { goToPackagesPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToPackagesPage";
import { isAnnual, isNoTouch, isTraditionalTrialUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { isMcpTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";

export enum EPageIdOption {
    TRAFFIC_AND_ENGAGEMENT = "trafficAndEngagement",
    FOLDERS_ANALYSIS = "folders.analysis",
    POPULAR_PAGES_ANALYSIS_V2 = "popular.pages.analysis.v2",
}

const UPSALE_BANNER_EVENT_NAMES: Partial<Record<string, { click: string; show: string }>> = {
    [EPageIdOption.FOLDERS_ANALYSIS]: {
        click: "folderAnalysis/folderUpsaleBanner/click",
        show: "foldersAanalysis/folderUpsaleBanner/show",
    },
    [EPageIdOption.TRAFFIC_AND_ENGAGEMENT]: {
        click: "trafficAndEngagement/segmentsUpsaleBanner/click",
        show: "trafficAndEngagement/segmentsUpsaleBanner/show",
    },
    [EPageIdOption.POPULAR_PAGES_ANALYSIS_V2]: {
        click: "popularPagesAnalysisV2/segmentsUpsaleBanner/click",
        show: "popularPagesAnalysisV2/segmentsUpsaleBanner/show",
    },
};

interface ISegmentsUpsaleBanner {
    isVisible: boolean;
    isTopBanner?: boolean;
    pageId: string;
    closeBanner: () => void;
}
const PERIOD_12 = 12;
const PERIOD_15 = 15;

export const SegmentsUpsaleBanner: FC<ISegmentsUpsaleBanner> = ({ isVisible, pageId, closeBanner, isTopBanner }) => {
    // Mark if the user has seen the banner. used for logging in mixpanel
    const [hasDisplayedToUser, setHasDisplayedToUser] = useState(false);
    const { params } = useCurrentStateAndParams();
    const { key: website } = params;
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();

    const isMidTierUser = SegmentsUtils.isMidTierUser();
    const isMcpTrialOrAfterTrialUser = isMcpTrialOrAfterTrial(swSettings);
    const isTrialUser = isTraditionalTrialUser(swSettings);
    const isNTAnnualUser = isNoTouch(swSettings) && isAnnual(swSettings);
    const hasHistoricalAddOns = swSettings?.current?.resources?.SnapshotInterval?.count >= PERIOD_12;
    const isNewDesignUser = (isTrialUser || isNTAnnualUser) && !hasHistoricalAddOns;
    const isNewDesign = pageId === EPageIdOption.TRAFFIC_AND_ENGAGEMENT && isNewDesignUser;

    const onButtonClick = useCallback(() => {
        if (isNewDesign) {
            allTrackers.trackEvent("button", "click", `${pageId}/HistoricalDataHook/click`);
        } else {
            const eventName = UPSALE_BANNER_EVENT_NAMES[pageId]?.click;
            if (eventName) {
                allTrackers.dispatchTrackEvent("Button", "click", eventName);
            }
        }
        if (isMcpTrialOrAfterTrialUser) {
            openHooksModalV5("ContactUs", undefined, "ContactUsSegmentsUpsaleBanner", "GetLoading");
            return;
        }

        const isSalesApp = proNavigator.isSalesSolution();

        if (isSalesApp) {
            window.open(`${getSiPackageHref()}?&hook=HistoricalData`);
            return;
        }

        if (isTrialUser) {
            goToPackagesPage();
            return;
        }

        if (isNTAnnualUser) {
            handlePurchaseAddonClick(historyHookPreselect, hasHistoricalAddOns ? PERIOD_15 : PERIOD_12);

            return;
        }

        if (isMidTierUser) {
            SegmentsUtils.openMidTierUserUpsellModal();
            return;
        }

        // Go to Segments homepage
        void proNavigator.go("digitalsuite_segments-homepage");
    }, [
        isNewDesign,
        isMcpTrialOrAfterTrialUser,
        isTrialUser,
        isNTAnnualUser,
        isMidTierUser,
        pageId,
        hasHistoricalAddOns,
        handlePurchaseAddonClick,
    ]);

    const logUserImpression = useCallback(() => {
        // In case of multiple renders of the component, we want to log the event where
        // the user has seen the banner only once.
        if (!hasDisplayedToUser) {
            const eventName = UPSALE_BANNER_EVENT_NAMES[pageId]?.show;
            if (eventName) {
                allTrackers.dispatchTrackEvent("Topbar", "show", eventName);
            }
            setHasDisplayedToUser(true);
        }
    }, [hasDisplayedToUser]);

    const upsaleButtonTextKey = isMidTierUser || isNewDesign ? "segments.upsale.banner.v2.buttonText" : "try.it.now";
    const buttonTextKey = isMcpTrialOrAfterTrialUser
        ? "segments.upsale.banner.contactUs.buttonText"
        : upsaleButtonTextKey;

    const buttonType = isMidTierUser ? "upsell" : "primary";
    const titleKey = isNewDesign ? "segments.upsale.banner.v2.title" : "segments.upsale.banner.title";

    const Subtitle = useMemo(
        () => (
            <SubtitleWrapper>
                {isNewDesign ? (
                    <div data-automation="banner-subtitle-part1">
                        {i18nFilter()("segments.upsale.banner.v2.subtitle", { website })}
                    </div>
                ) : (
                    <>
                        <div data-automation="banner-subtitle-part1">
                            {i18nFilter()("segments.upsale.banner.subtitle.part1")}
                        </div>
                        <div data-automation="banner-subtitle-part2">
                            {i18nFilter()("segments.upsale.banner.subtitle.part2")}
                        </div>
                    </>
                )}
            </SubtitleWrapper>
        ),
        [isNewDesign, website],
    );

    const CustomIcon = useMemo(
        () =>
            isNewDesign ? (
                <StyledGIF
                    src={AssetsService.assetUrl("/images/segments-upsale-banner-animation.gif")}
                    alt="benchmark"
                />
            ) : (
                <SWReactIcons
                    iconName="segment-analysis"
                    size="lg"
                />
            ),
        [isNewDesign],
    );

    return (
        isVisible && (
            <BannerWrapper isTopBanner={isTopBanner}>
                <StyledBanner
                    isNewDesign={isNewDesign}
                    title={i18nFilter()(titleKey, { website })}
                    subtitle={Subtitle}
                    buttonType={buttonType}
                    buttonText={i18nFilter()(buttonTextKey)}
                    onButtonClick={onButtonClick}
                    CustomIcon={CustomIcon}
                    onInitialRender={logUserImpression}
                    onCloseClick={closeBanner}
                />
            </BannerWrapper>
        )
    );
};
